'use client'

import useGameStore from '../../stores/gameStore'
import { KawaiiIcon } from './KawaiiIcon'
import { ui, kawaii, stitchInset } from '../../lib/ui/theme'
import { useIsMobile } from '../../lib/ui/useIsMobile'

// Server sends raw "need" values (0-1, higher = more urgent).
// We display them as SATISFACTION levels (full bar = fulfilled, empty = needs attention).
// Each stat definition picks its source from the NPC state.
//
// Sources:
//   need      - inverse of npc.needs[key]     (need=0 → full bar)
//   valence   - npc.mood.valence  mapped [-1,1] → [0,1]  (1 = joy)
//   calm      - npc.mood.arousal  mapped [-1,1] → [1,0]  (1 = calm/still)
//
// Joy + Calm are the two MOOD axes (valence × arousal — the circumplex):
// Joy = pleasant↔unpleasant, Calm = still↔keyed-up. Calm reads from arousal
// (not the near-static `safety` need, which sat pinned full) so it actually
// breathes — a rave or a gamble drains it, sleep/paddle/a quiet film fill it.
//
// Colours are the bar fills and are also the dot beside each label
// (see KawaiiIcon).
const STATS = [
  { key: 'hunger',    label: 'Fullness', icon: 'fullness', color: '#5ec26a', source: 'need' },
  { key: 'rest',      label: 'Energy',   icon: 'energy',   color: '#f5b13d', source: 'need' },
  { key: 'curiosity', label: 'Wonder',   icon: 'wonder',   color: '#a855f7', source: 'need' },
  { key: 'valence',   label: 'Joy',      icon: 'joy',      color: '#facc15', source: 'valence' },
  { key: 'arousal',   label: 'Calm',     icon: 'calm',     color: '#4ec3f0', source: 'calm' },
]

function Bar({ pct, color, height = 11 }) {
  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: 999,
        background: kawaii.creamDeep,
        border: kawaii.outlineThin,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          right: 'auto',
          width: `${Math.round(pct * 100)}%`,
          background: color,
          borderRadius: 999,
          // A lighter top edge so the fill reads as a rounded object, the way
          // a game meter does, rather than a flat block of colour.
          backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.45), rgba(255,255,255,0) 60%)',
          transition: 'width 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />
    </div>
  )
}

/**
 * Top-centre needs card. Cream, outlined, stitched, one column per stat with
 * an icon, a label and a meter. This is the "how is he doing" glance.
 */
export function NPCStatsHUD() {
  // The five displayed percentages, quantised to whole percent and
  // joined into one string, so the ten-a-second npcStates churn only
  // re-renders this card when a bar would visibly move. It selected the
  // whole npcStates slice before, which meant rebuilding five icon
  // columns at 10Hz on the same thread as the render loop.
  const pctKey = useGameStore((s) => {
    const npc = s.npcStates?.[0]
    if (!npc) return ''
    const n = npc.needs || {}
    const m = npc.mood || {}
    const clamp = (x) => Math.max(0, Math.min(1, x))
    // the mind's, while one is connected (npc.mind, sim-server npc/NPC.js mindView): how rested he is, his mood, his
    // want of something new, and how rattled he is. hunger is the body's, which is the town's to keep.
    // (Energy was the mind's energy, how keyed up he is from -1 to 1, drawn from the middle: an ordinary moment is 0 on
    // it, so the bar sat at half or under 64% of a day and never went past 67%, and people read it as a bird running
    // on empty, 30 Oct. an energy bar is read as a battery, so it is how rested he is now: full when he wakes, going
    // down through his day)
    const mind = npc.mind
    if (mind && typeof mind.mood === 'number' && typeof mind.energy === 'number') {
      const mn = mind.needs || {}
      return [
        clamp(1 - (n.hunger ?? 0)), clamp(1 - (mn.rest ?? n.rest ?? 0)), clamp(1 - (mn.curiosity ?? 0)),
        clamp((mind.mood + 1) / 2), clamp(1 - (mn.quiet ?? 0)),
      ].map((v) => Math.round(v * 100)).join(',')
    }
    return [
      clamp(1 - (n.hunger ?? 0)), clamp(1 - (n.rest ?? 0)), clamp(1 - (n.curiosity ?? 0)),
      clamp(((m.valence ?? 0) + 1) / 2), clamp((1 - (m.arousal ?? 0)) / 2),
    ].map((v) => Math.round(v * 100)).join(',')
  })
  const pcts = pctKey ? pctKey.split(',').map(Number) : null
  // Phone sizes are a set, not a scale factor. At 375px five columns get
  // about 55 usable px each, and the desktop type in that space truncated
  // to "F..." — a meter labelled with a letter measures nothing.
  const mobile = useIsMobile()
  const sz = mobile
    ? { icon: 15, font: 10, bar: 8, pad: '7px 4px 9px', cellPad: 'px-1.5', gap: 'gap-1', mb: 'mb-1' }
    : { icon: 22, font: 12.5, bar: 11, pad: '10px 8px 12px', cellPad: 'px-2 md:px-3', gap: 'gap-1.5', mb: 'mb-1.5' }

  // Order must match the selector above: hunger, rest, curiosity,
  // valence, calm.
  const PCT_INDEX = { hunger: 0, rest: 1, curiosity: 2 }
  const getPct = (stat) => {
    if (!pcts) return 0
    if (stat.source === 'valence') return pcts[3] / 100
    if (stat.source === 'calm') return pcts[4] / 100
    return (pcts[PCT_INDEX[stat.key]] ?? 0) / 100
  }

  return (
    <div className="fixed top-3 md:top-4 left-1/2 -translate-x-1/2 z-40 pointer-events-none w-[calc(100vw-1.5rem)] max-w-[540px]">
      <div style={{ position: 'relative', background: kawaii.cream, border: kawaii.outline, borderRadius: kawaii.radius, boxShadow: kawaii.drop, padding: sz.pad }}>
        <div style={stitchInset(kawaii.radius)} />
        <div className="flex items-stretch">
          {STATS.map((stat, i) => (
            <div
              key={stat.key}
              className={`flex-1 ${sz.cellPad} min-w-0`}
              style={{
                borderRight: i === STATS.length - 1 ? 'none' : kawaii.stitch,
              }}
            >
              <div className={`flex items-center ${sz.gap} ${sz.mb} min-w-0 justify-center md:justify-start`}>
                <span className="shrink-0"><KawaiiIcon name={stat.icon} color={stat.color} size={sz.icon} /></span>
                <span
                  style={{
                    color: kawaii.ink,
                    fontFamily: ui.font,
                    fontSize: sz.font,
                    fontWeight: 700,
                    letterSpacing: mobile ? 0 : '0.01em',
                  }}
                >
                  {stat.label}
                </span>
              </div>
              <Bar pct={getPct(stat)} color={stat.color} height={sz.bar} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default NPCStatsHUD
